import type { NormalizedThinkingEvent } from './types';
import { ThinkingStepItem } from './ThinkingStepItem';

interface ThinkingStepListProps {
  events: NormalizedThinkingEvent[];
  isStreaming: boolean;
}

/**
 * Coalesce duplicate thinking events into a single entry per logical step.
 * The backend emits multiple events for the same step via different channels
 * (planner.status_update + thinking.step_timing + specific events).
 * We keep only the most informative version of each logical step.
 */
function deduplicateEvents(events: NormalizedThinkingEvent[]): NormalizedThinkingEvent[] {
  const seen = new Map<string, NormalizedThinkingEvent>();
  const result: NormalizedThinkingEvent[] = [];

  for (const event of events) {
    const stepKey = resolveStepKey(event);

    if (!stepKey) {
      result.push(event);
      continue;
    }

    const existing = seen.get(stepKey);
    if (!existing) {
      seen.set(stepKey, event);
      result.push(event);
      continue;
    }

    const existingIdx = result.indexOf(existing);
    const better = pickBetter(existing, event);
    if (better !== existing && existingIdx !== -1) {
      result[existingIdx] = better;
      seen.set(stepKey, better);
    } else if (better !== existing) {
      seen.set(stepKey, better);
    }
  }

  return result;
}

const STEP_KEY_ALIASES: Record<string, string> = {
  'session.started': '_session',
  'guardrail.input': '_guardrail_input',
  'guardrail.output': '_guardrail_output',
  'pipeline.patient_history': '_patient_history',
  /** Planner SSE uses `planner.<PlannerStepName>`; post-plan timings use `planner.build_plan`. */
  'planner.planning': '_planner',
  'planner.build_plan': '_planner',
  'planner.loading_patient_context': '_patient_history',
  'interpreter.route_classification': '_interpreter_route',
  'interpreter.intent': '_interpreter_intent',
  'interpreter.extract': '_interpreter_extract',
  'interpreter.tool_selection': '_interpreter_tool',
  'interpreter.fast_path': '_interpreter_fast_path',
  'orchestrator.step-final': '_final_response',
  /** Final tokenization vs orchestrator final step — one row in the UI. */
  'response.generate': '_final_response',
};

/** Normalize `tool.get_patient_context` ↔ title-derived `_tool_patient_context` from "Loading patient context". */
function canonicalToolDedupeKey(toolId: string): string {
  const id = toolId.replace(/^tool\./, '').trim();
  if (id === 'get_patient_context') return '_patient_history';
  return `_tool_${id}`;
}

function resolveStepKey(event: NormalizedThinkingEvent): string | null {
  const raw = event.raw as Record<string, unknown> | undefined;
  const stepKey = raw?.step_key as string | undefined;
  if (stepKey) {
    if (stepKey in STEP_KEY_ALIASES) return STEP_KEY_ALIASES[stepKey];
    if (stepKey.startsWith('tool.')) return canonicalToolDedupeKey(stepKey);
    if (stepKey.startsWith('orchestrator.')) {
      const t = (event.title || '').toLowerCase();
      if (t.includes('generating recommendation') || t.includes('recommendation ready')) return '_generate_recommendation';
      if (t.includes('validating output')) return '_validate_output';
      if (
        t.includes('preparing response') ||
        t.includes('preparing final response') ||
        t.includes('generating final response') ||
        t.includes('generating response')
      ) {
        return '_final_response';
      }
      return stepKey;
    }
    return stepKey;
  }

  const title = event.title.toLowerCase();

  if (title.includes('session started')) return '_session';
  if (title.includes('safety check') || title.includes('inbound safety')) return '_guardrail_input';
  if (title.includes('output safety') || title.includes('output check')) return '_guardrail_output';
  if (title.includes('building execution plan') || title.includes('execution plan')) return '_planner';
  if (title.includes('loading patient history') || title.includes('loading patient context')) return '_patient_history';
  if (title.includes('interpreting request')) return '_interpreter_route';
  if (title.includes('classifying intent')) return '_interpreter_intent';
  if (title.includes('extracting instruction')) return '_interpreter_extract';
  if (title.includes('selecting tool')) return '_interpreter_tool';
  if (title.includes('fast path')) return '_interpreter_fast_path';
  if (title.includes('execution plan ready')) return '_plan_ready';
  if (title.includes('fetching patient data')) return '_fetching_data';
  if (title.includes('invoking specialty')) return '_specialty_agents';
  if (title.includes('preparing final response') || title.includes('final response')) return '_final_response';
  if (title.includes('generating recommendation') || title.includes('recommendation ready')) return '_generate_recommendation';
  if (title.includes('validating output')) return '_validate_output';
  if (title.includes('generating final response') || title.includes('generating response')) return '_final_response';
  if (title.includes('handoff')) return '_handoff';
  if (title.includes('processing')) return '_processing';
  if (title.includes('analysis complete')) return '_analysis_complete';

  const toolMatch =
    title.match(/(?:running|executing)\s+(?:tool:\s*)?(.+?)(?:\s*[—–-]\s*(?:done|finished|error|failed))?$/i) ||
    title.match(/^(?:fetching|loading|checking|analyzing)\s+(.+?)(?:\s*[—–-]\s*(?:done|finished))?$/i);
  if (toolMatch) {
    let toolName = (toolMatch[1] || '').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();
    if (toolName === 'patient_context') toolName = 'get_patient_context';
    if (toolName) return canonicalToolDedupeKey(`tool.${toolName}`);
  }

  return null;
}

function pickBetter(a: NormalizedThinkingEvent, b: NormalizedThinkingEvent): NormalizedThinkingEvent {
  const statusRank = (s: string) =>
    s === 'success' ? 3 : s === 'warning' ? 2 : s === 'error' ? 2 : s === 'running' ? 1 : 0;
  const aRank = statusRank(a.status);
  const bRank = statusRank(b.status);
  if (bRank > aRank) return b;
  if (aRank > bRank) return a;
  if (b.durationMs != null && a.durationMs == null) return b;
  if (a.durationMs != null && b.durationMs == null) return a;
  if (b.durationMs != null && a.durationMs != null && b.durationMs > a.durationMs) return b;
  if (b.toolName && !a.toolName) return b;
  return b;
}

export function ThinkingStepList({ events, isStreaming }: ThinkingStepListProps) {
  if (events.length === 0 && isStreaming) {
    return (
      <div className="py-2 flex items-center gap-2">
        <span className="thinking-pulse-dot" />
        <span className="text-[11px] text-text-tertiary">Initializing…</span>
      </div>
    );
  }

  const deduplicated = deduplicateEvents(events);

  return (
    <div className="py-1 space-y-0.5">
      {deduplicated.map((event, i) => (
        <ThinkingStepItem
          key={event.id}
          event={event}
          isLast={i === deduplicated.length - 1}
        />
      ))}
    </div>
  );
}
